import { useState, useEffect, useCallback, useContext, useRef } from "react";
import { toast } from "react-toastify";
import { getLogger } from "@hoteltradergit/ht-telemetry-ui-lib";

import { getPowerBiEmbedConfig } from "../api";
import {
  FILTERS_OPERATIONS,
  POWER_BI_SETTINGS,
  TOKEN_TYPE,
} from "../constants";
import { PropertyStoreContext } from "../contexts/PropertyStoreContext";
import { getPowerBIPropertyFilter } from "../constants/utils";

// Shared cache for PowerBI config - fetched once and shared across all hook instances
const powerBIConfigCache = {
  reports: null,
  accessToken: null,
  tokenExpiration: null,
  isFetching: false,
  hasFetched: false,
  propertyCode: null,
  cacheVersion: 0, // Incremented when cache is updated to trigger re-renders
  refreshIntervalId: null, // Global interval ID
};

/**
 * Custom hook for PowerBI report embedding
 * @param {string} reportTitle - Component/accessibility identifier, can be descriptive (e.g., "Q4 Performance Dashboard").
 *                                Used for screen readers and PowerBI's internal component tracking.
 * @param {Array} additionalEventHandlers - Additional event handlers [[name, handler], ...]
 * @param {string} reportName - Name of the report to embed (e.g., "Supplier Portal Report", "Supplier Portal Home Page")
 * @param {string} pageName - Optional: Exact page name from PowerBI to navigate to (e.g., "Booking Pace").
 *                             This should match the page name exactly as it appears in the PowerBI report.
 * @returns {object} PowerBI embed state and handlers
 */
const usePowerBIEmbed = (
  reportTitle = "Report",
  additionalEventHandlers = [],
  reportName = null,
  pageName = null
) => {
  const { selectedProperty } = useContext(PropertyStoreContext);

  const [report, setReport] = useState();
  const [isEmbedded, setIsEmbedded] = useState(false);
  const [isBootstrapped, setIsBootstrapped] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  const hasInitializedRef = useRef(false);
  const [cacheReady, setCacheReady] = useState(false);
  // Stores the current additional filter (e.g., date filter) as an array with a single filter
  const additionalFiltersRef = useRef([]);

  // Reset state when component unmounts to ensure clean state on remount
  useEffect(() => {
    return () => {
      hasInitializedRef.current = false;
    };
  }, []);

  // Base event handlers - use useCallback to avoid stale closures
  const handleLoaded = useCallback(() => {
    getLogger().info("usePowerBIEmbed", {
      type: "Info",
      page_url: window.location.href,
      error_desc: `Report loaded event fired for: ${reportTitle}`,
    });
    setIsLoaded(true);
  }, [reportTitle]);

  const handleError = useCallback(
    (event) => {
      if (event) {
        getLogger().error("usePowerBIEmbed", {
          type: "Error",
          page_url: window.location.href,
          error_desc: `Error event for ${reportTitle}`,
          error_stack: event.detail?.stack ?? null,
        });
      }
    },
    [reportTitle]
  );

  // Create event handlers map
  const [eventHandlersMap] = useState(() => new Map());

  // Update event handlers map when handlers change
  useEffect(() => {
    eventHandlersMap.clear();
    eventHandlersMap.set("loaded", handleLoaded);
    eventHandlersMap.set("error", handleError);
    additionalEventHandlers.forEach(([eventName, handler]) => {
      eventHandlersMap.set(eventName, handler);
    });
  }, [eventHandlersMap, handleLoaded, handleError, additionalEventHandlers]);

  // Bootstrap config - minimal config to initialize iframe early
  const [bootstrapConfig, setBootstrapConfig] = useState({
    type: "report",
    embedUrl: undefined,
    hostname: "https://app.powerbi.com",
    settings: POWER_BI_SETTINGS,
  });

  // Full embed config - complete config with token for actual embedding
  const [reportConfig, setReportConfig] = useState({
    type: "report",
    embedUrl: undefined,
    tokenType: TOKEN_TYPE.Embed,
    accessToken: undefined,
    settings: POWER_BI_SETTINGS,
  });

  // Fetch PowerBI config once when property first becomes available (not on every property change)
  useEffect(() => {
    const propertyCode = selectedProperty?.propertyCode;

    // Skip if no property available yet
    if (!propertyCode?.length > 0) {
      return;
    }

    // Skip if config already fetched (don't refetch on property changes)
    // The config is shared across all properties, so we only need to fetch it once
    if (powerBIConfigCache.hasFetched || powerBIConfigCache.isFetching) {
      return;
    }

    // Fetch config only once
    const fetchConfig = async () => {
      powerBIConfigCache.isFetching = true;
      powerBIConfigCache.propertyCode = propertyCode;

      try {
        const response = await getPowerBiEmbedConfig();

        // Handle new API response structure with success/error fields
        const apiResponse = response?.data || response;

        // Check if API call was successful
        if (apiResponse?.success === false || apiResponse?.error) {
          const errorMessage =
            apiResponse?.error ||
            apiResponse?.message ||
            "Unknown error occurred";
          getLogger().error("usePowerBIEmbed", {
            type: "Error",
            page_url: window.location.href,
            error_desc: "PowerBI config API error",
            error_stack: apiResponse?.stack ?? null,
          });
          toast.error(`Failed to fetch PowerBI config: ${errorMessage}`);
          powerBIConfigCache.isFetching = false;
          return;
        }

        // Extract data from new response structure: {success, message, data: {reports, embedToken}, error}
        const responseData = apiResponse?.data || apiResponse;
        const reports = responseData?.reports || [];
        const tokenExpiration = responseData?.embedToken?.expiration;
        const embedToken = responseData?.embedToken;
        const accessToken = embedToken?.token;

        if (!accessToken) {
          getLogger().error("usePowerBIEmbed", {
            type: "Error",
            page_url: window.location.href,
            error_desc: "No access token in PowerBI config response",
            error_stack: response?.stack ?? null,
          });
          toast.error(`Failed to fetch access token for PowerBI reports.`);
          powerBIConfigCache.isFetching = false;
          return;
        }

        if (!reports || reports.length === 0) {
          getLogger().error("usePowerBIEmbed", {
            type: "Error",
            page_url: window.location.href,
            error_desc: "No reports in PowerBI config response",
            error_stack: response?.stack ?? null,
          });
          toast.error(`No reports found in PowerBI config.`);
          powerBIConfigCache.isFetching = false;
          return;
        }

        // Store all reports, token and expiration in cache
        powerBIConfigCache.reports = reports;
        powerBIConfigCache.accessToken = accessToken;
        powerBIConfigCache.tokenExpiration = tokenExpiration;
        powerBIConfigCache.embedToken = embedToken; // Store full token object for potential future use
        powerBIConfigCache.hasFetched = true;

        // Trigger re-render in all hook instances by updating a shared flag
        // This will cause the second effect to re-run and pick up the cache
        powerBIConfigCache.cacheVersion =
          (powerBIConfigCache.cacheVersion || 0) + 1;
      } catch (error) {
        getLogger().error("usePowerBIEmbed", {
          type: "Error",
          page_url: window.location.href,
          error_desc: "Error fetching PowerBI embed config",
          error_stack: error?.stack ?? null,
        });
        toast.error(
          `Failed to fetch PowerBI config: ${error.message || "Unknown error"}`
        );
      } finally {
        powerBIConfigCache.isFetching = false;
      }
    };

    fetchConfig();
  }, [selectedProperty?.propertyCode]);

  // Monitor cache readiness - this effect polls for cache availability
  useEffect(() => {
    const propertyCode = selectedProperty?.propertyCode;

    if (!propertyCode?.length > 0) {
      setCacheReady(false);
      return;
    }

    // Check if cache is ready immediately (config is shared across all properties)
    if (powerBIConfigCache.hasFetched && powerBIConfigCache.accessToken) {
      setCacheReady(true);
      return;
    }

    // Reset initialization when property changes (but don't refetch config)
    if (powerBIConfigCache.propertyCode !== propertyCode) {
      hasInitializedRef.current = false;
      setIsEmbedded(false);
    }

    // Poll for cache readiness (check every 100ms)
    setCacheReady(false);
    const interval = setInterval(() => {
      if (powerBIConfigCache.hasFetched && powerBIConfigCache.accessToken) {
        setCacheReady(true);
        clearInterval(interval);
      }
    }, 100);

    return () => clearInterval(interval);
  }, [selectedProperty?.propertyCode]);

  // PHASE 1: Bootstrap - Set up minimal config early (before token is available)
  // This initializes the iframe and prepares it for embedding
  useEffect(() => {
    // Skip if no report name specified (can't determine embedUrl without report)
    if (!reportName) {
      return;
    }

    // Skip if already bootstrapped
    if (isBootstrapped) {
      return;
    }

    // Wait for cache to have reports data
    if (!powerBIConfigCache.hasFetched || !powerBIConfigCache.reports) {
      return;
    }

    const reports = powerBIConfigCache.reports || [];

    // Try to find the report in cache
    let selectedReport = null;
    if (reports.length > 0) {
      selectedReport =
        reports.find((report) => report.name === reportName) ||
        reports.find(
          (report) => report.name?.toLowerCase() === reportName.toLowerCase()
        );
    }

    // If we have the report info, set up bootstrap config
    if (selectedReport?.embedUrl) {
      setBootstrapConfig({
        type: "report",
        embedUrl: selectedReport.embedUrl,
        hostname: "https://app.powerbi.com",
        settings: POWER_BI_SETTINGS,
      });
      setIsBootstrapped(true);
    }
  }, [reportName, isBootstrapped, cacheReady]);

  // PHASE 2: Full Embed - Set up complete config with token when cache is ready
  useEffect(() => {
    const propertyCode = selectedProperty?.propertyCode;

    // Skip if no property
    if (!propertyCode?.length > 0) {
      return;
    }

    // Skip if cache not ready yet
    if (
      !cacheReady ||
      !powerBIConfigCache.hasFetched ||
      !powerBIConfigCache.accessToken
    ) {
      return;
    }

    // Skip if already initialized for this report
    if (hasInitializedRef.current && isEmbedded) {
      return;
    }

    const reports = powerBIConfigCache.reports || [];
    const accessToken = powerBIConfigCache.accessToken;

    // Find the report by name if reportName is provided, otherwise use the first report
    let selectedReport = null;
    if (reportName) {
      // Try exact match first, then case-insensitive match
      selectedReport =
        reports.find((report) => report.name === reportName) ||
        reports.find(
          (report) => report.name?.toLowerCase() === reportName.toLowerCase()
        );

      if (!selectedReport) {
        getLogger().error("usePowerBIEmbed", {
          type: "Error",
          page_url: window.location.href,
          error_desc: `Report "${reportName}" not found. Available: ${reports
            .map((r) => r.name)
            .join(", ")}`,
        });
        toast.error(
          `Report "${reportName}" not found. Available reports: ${reports.map((r) => r.name).join(", ")}`
        );
        setIsEmbedded(true);
        return;
      }
    } else if (reports.length > 0) {
      // Fallback to first report if no reportName specified
      selectedReport = reports[0];
    }

    if (!selectedReport?.embedUrl) {
      toast.error(`Failed to get embed URL for ${reportTitle}.`);
      setIsEmbedded(true);
      return;
    }

    const filters = [getPowerBIPropertyFilter(propertyCode)];

    // Find the page ID if pageName is provided
    let pageNameToEmbed;
    if (pageName && selectedReport.pages) {
      const targetPage = selectedReport.pages.find(
        (page) => page.name?.toLowerCase() === pageName.toLowerCase()
      );
      if (targetPage) {
        pageNameToEmbed = targetPage.name;
        getLogger().info("usePowerBIEmbed", {
          type: "Info",
          page_url: window.location.href,
          error_desc: `Will embed with specific page: ${pageNameToEmbed}`,
        });
      }
    }

    setReportConfig({
      type: "report",
      embedUrl: selectedReport.embedUrl,
      tokenType: TOKEN_TYPE.Embed,
      accessToken,
      filters,
      pageName: pageNameToEmbed, // Include page name in config
      settings: POWER_BI_SETTINGS,
    });
    setIsEmbedded(true);
    setIsLoaded(false); // Will be set to true by "loaded" event
    hasInitializedRef.current = true;
  }, [
    selectedProperty?.propertyCode,
    reportName,
    pageName,
    reportTitle,
    isEmbedded,
    cacheReady,
  ]);

  // Set report title
  useEffect(() => {
    if (report) {
      report.setComponentTitle(reportTitle);
    }
  }, [report, reportTitle]);

  // Navigate to specific page if pageName is provided
  useEffect(() => {
    const navigateToPage = async () => {
      if (!report || !pageName || !isLoaded) {
        return;
      }

      try {
        const pages = await report.getPages();
        // Find the page by name (case-insensitive)
        const targetPage = pages.find(
          (page) => page.displayName.toLowerCase() === pageName.toLowerCase()
        );

        if (targetPage) {
          if (!targetPage.isActive) {
            await targetPage.setActive();
          }
        } else {
          getLogger().warn("usePowerBIEmbed", {
            type: "Warn",
            page_url: window.location.href,
            error_desc: `Page "${pageName}" not found in report "${reportName}". Available: ${pages
              .map((p) => p.displayName)
              .join(", ")}`,
          });
        }
      } catch (error) {
        getLogger().error("usePowerBIEmbed", {
          type: "Error",
          page_url: window.location.href,
          error_desc: `Error navigating to page "${pageName}" for ${reportTitle}`,
          error_stack: error?.stack ?? null,
        });
      }
    };

    navigateToPage();
  }, [report, pageName, isLoaded, reportName, reportTitle]);

  // Consolidated helper function to apply all filters (property + additional)
  const applyAllFilters = useCallback(
    async (propertyCode, additionalFilters = []) => {
      if (!report || !propertyCode) {
        return;
      }

      try {
        const propertyFilter = getPowerBIPropertyFilter(propertyCode);
        const allFilters = [propertyFilter, ...additionalFilters];
        await report.updateFilters(FILTERS_OPERATIONS.ReplaceAll, allFilters);
      } catch (error) {
        getLogger().error("usePowerBIEmbed", {
          type: "Error",
          page_url: window.location.href,
          error_desc: "Error updating PowerBI filters",
          error_stack: error?.stack ?? null,
        });
        if (error) {
          toast.error(
            typeof error === "string" ? error : "Failed to update filters"
          );
        }
      }
    },
    [report]
  );

  // Apply filter to report - stores additional filter and applies all filters
  const applyFilter = useCallback(
    async (filter) => {
      if (!selectedProperty?.propertyCode) {
        return;
      }

      // Store the additional filter
      additionalFiltersRef.current = filter ? [filter] : [];

      // Apply all filters using consolidated function
      await applyAllFilters(
        selectedProperty.propertyCode,
        additionalFiltersRef.current
      );
    },
    [selectedProperty, applyAllFilters]
  );

  // Apply property filter when property changes
  useEffect(() => {
    if (!report || !selectedProperty?.propertyCode) {
      return;
    }

    // Apply all filters (property + any stored additional filters)
    applyAllFilters(
      selectedProperty.propertyCode,
      additionalFiltersRef.current
    );
  }, [selectedProperty, report, applyAllFilters]);

  // Token refresh when report is loaded and token about to expire
  useEffect(() => {
    if (!isLoaded || !powerBIConfigCache.tokenExpiration || !report) return;

    // Only create interval if one doesn't already exist globally
    if (!powerBIConfigCache.refreshIntervalId) {
      const refreshInterval = 55 * 60 * 1000; // Refresh every 55 minutes (5 minutes before 1-hour expiration)

      const refreshToken = async () => {
        try {
          const response = await getPowerBiEmbedConfig();
          const responseData =
            response?.data?.data || response?.data || response;
          const newToken = responseData?.embedToken?.token;
          const newExpiration = responseData?.embedToken?.expiration;

          if (newToken) {
            // Extract all data same as initial setup
            const reports = responseData?.reports || [];
            const embedToken = responseData?.embedToken;

            // Store all data in cache exactly like initial setup
            powerBIConfigCache.reports = reports;
            powerBIConfigCache.accessToken = newToken;
            powerBIConfigCache.tokenExpiration = newExpiration;
            powerBIConfigCache.embedToken = embedToken; // Store full token object for potential future use

            // CRITICAL: Update token on embedded report directly to avoid re-embed
            // This is required for powerbi-client-react v2.0+ which re-embeds on config changes
            if (report) {
              try {
                await report.setAccessToken(newToken);
              } catch (tokenError) {
                // Non-fatal: cache is updated, other instances or next embed will use new token.
                // Logged through the telemetry logger — an unguarded
                // window.R7Insight.error here would itself throw inside this
                // catch when the CDN script is unavailable.
                getLogger().error("powerbi.token_update_failed", {
                  type: "Error",
                  page_url: window.location.href,
                  error_desc: `[usePowerBIEmbed] Failed to update token on report: ${
                    tokenError?.message ?? String(tokenError)
                  }`,
                });
              }
            }
          } else {
            throw new Error("No access token found in PowerBI API response");
          }
        } catch (error) {
          getLogger().error("usePowerBIEmbed", {
            type: "Error",
            page_url: window.location.href,
            error_desc: "Token refresh failed",
            error_stack: error?.stack ?? null,
          });
          const errorMessage =
            error?.message || error?.toString() || "Unknown error";
          toast.error(`PowerBI token refresh failed: ${errorMessage}`);
        }
      };

      // Set up global recurring token refresh
      powerBIConfigCache.refreshIntervalId = setInterval(
        refreshToken,
        refreshInterval
      );
    }

    // Cleanup: clear global interval when last component unmounts
    return () => {};
  }, [isLoaded, report, reportTitle]);

  const handleEmbedComponent = (embedObject) => {
    setReport(embedObject);
  };

  return {
    report,
    bootstrapConfig,
    reportConfig,
    isBootstrapped,
    isEmbedded,
    isLoaded,
    eventHandlersMap,
    handleEmbedComponent,
    applyFilter,
  };
};

export default usePowerBIEmbed;
